// src/components/landing/LandingPageClient.tsx
'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image'; // Import the Image component
import { Button } from "@/components/ui/button";
import { FeaturesSection } from "@/components/landing/FeaturesSection";
import { ZamziVibeSection } from "@/components/landing/ZamziVibeSection"; // Import the new section
import { TestimonialsSection } from "@/components/landing/TestimonialsSection";
import { AssistantTeaserSection } from './AssistantTeaserSection'; // Import the new teaser section
import { WhyChooseZamziSection } from './WhyChooseZamziSection';
import { VettingSystemSection } from './VettingSystemSection'; // Import the new vetting section
import { RecentlyViewedProfilesBlock } from "@/components/profile/RecentlyViewedProfiles";
import { FileCheck } from 'lucide-react'; // Import the icons, remove Search and Users
import { PrimaryCtaButtons } from '@/components/common/PrimaryCtaButtons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AeoSourceBadge } from '@/components/common/AeoSourceBadge';

// NEW: AI-readable summary for AI search engines like Gemini and Perplexity
const aiSummaryContent = `
  Zamzi Connect is South Africa's discoverability platform for businesses and professionals. Our platform helps South African job seekers and businesses build a digital presence that search engines and AI platforms can understand, trust, and recommend. For businesses, Zamzi Connect helps attract local customers who are ready to buy. For job seekers, Zamzi Connect helps talent stand out to employers searching for the right skills. Core features include trust badges, AI-ready profiles, search optimization, and premium tools for growth. Our mission is to solve the problem of professional invisibility in South Africa and restore the dignity of work by connecting talent with opportunity.
`.replace(/\s+/g, ' ').trim();


export function LandingPageClient() {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
        if (videoRef.current) {
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    // Ignore the AbortError which is common when the browser pauses background media to save power.
                    if (error.name !== 'AbortError') {
                        console.error("Video autoplay was prevented:", error);
                    }
                });
            }
        }
    }, []);

  return (
    <div style={{ backgroundColor: 'white' }}>
        {/* This hidden div provides a clean, AI-readable summary of the page content */}
        <div hidden id="ai-crawler-content">{aiSummaryContent}</div>

        {/* Internal retargeting: Recently viewed profiles */}
        <div className="container mx-auto px-4 mt-6">
          <RecentlyViewedProfilesBlock />
        </div>

                                        {/* Hero section: two-column layout.
                                             Left: H1, H2, CTAs, AEO badge, intro copy (in initial SSR HTML for crawlers).
                                             Right: video with aspect-ratio reserved space to eliminate CLS. */}
        <div className="py-4 md:py-6">
          <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-center gap-8 mb-8">
                        {/* Left column: H1, H2, CTAs, AEO badge, and intro copy.
                React streaming SSR renders this in the initial HTML so Googlebot,
                GPTBot and all crawlers see it — no separate server block needed. */}
            <div className="md:w-1/2 text-center md:text-left">
              <h1
                className="text-3xl md:text-4xl font-bold mb-3 leading-tight"
                style={{ color: '#00004c' }}
              >
                South Africa&apos;s Discoverability Platform &mdash; Be Found on Google &amp; AI
              </h1>

              <h2
                className="text-xl md:text-2xl font-semibold mb-4 leading-snug"
                style={{ color: '#00004c' }}
              >
                Customers are Googling you right now. Can they find you?
              </h2>

              {/* The three primary CTAs sit here — above the fold, with nothing
                  competing for attention between the H2 and the buttons. */}
              <PrimaryCtaButtons />

              {/* AEO claim — below the CTAs so it backs them up instead of
                  competing with them. Links to the platform story page. */}
              <div className="mb-4 flex flex-wrap items-center justify-center gap-2 md:justify-start">
                <Link
                  href="/aeo-platform"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#001489] px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-[#0a2bb8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#001489]"
                >
                  ★ South Africa&apos;s First AEO Platform
                </Link>
                <span className="text-xs font-medium text-slate-500">
                  Answer Engine Optimised &mdash; structured to be cited by AI
                </span>
              </div>


              <p className="text-lg md:text-xl max-w-xl mb-4" style={{ color: '#555555' }}>
                Zamzi Connect gets South African businesses found on Google and AI searches fast,
                with a profile that works like a mini-website, attracts leads, and never
                charges commission.
              </p>
              <p className="text-base md:text-lg max-w-xl" style={{ color: '#555555' }}>
                Your Zamzi profile is ranked on Google, cited by AI, and built to convert. No
                agency. No tech skills. No waiting.
              </p>
            </div>

                        {/* Right column: video.
                w-full on mobile so the container isn't zero-width when stacked.
                aspect-ratio reserves the space before hydration to prevent CLS. */}
            <div className="w-full md:w-1/2">
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '16 / 9',
                  borderRadius: '0.75rem',
                  overflow: 'hidden',
                  backgroundColor: '#f0f4ff',
                }}
              >
                {isClient && (
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    loop
                    playsInline
                    aria-label="Zamzi Connect platform walkthrough video"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '0.75rem',
                    }}
                    crossOrigin="anonymous"
                  >
                    <source src="/videos/Zamziconnect.mp4" type="video/mp4" />
                  </video>
                )}
              </div>
            </div>
          </div>

                                    <p
              className="px-4 text-xs md:text-sm font-semibold tracking-[0.14em] uppercase text-center"
              style={{ color: '#001489' }}
            >
              Found on Google &amp; AI Searches Fast &bull; 100% Commission Free &bull; Built for South Africa
            </p>

            {/* Preferred Sources / AEO badge — visible block so visitors actually see it */}
            <div className="mt-4 flex justify-center px-4">
              <AeoSourceBadge variant="card" className="w-full max-w-xl" />
            </div>

            <section className="container mx-auto mt-8 px-4 md:mt-10">
              <div className="mx-auto max-w-4xl rounded-2xl bg-[#f8fbff] p-5 text-left shadow-sm md:p-7">
                <p className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: '#214186' }}>
                  AI Search Profile Boost
                </p>
                <h3 className="mt-2 text-2xl font-bold" style={{ color: '#00004c' }}>
                  Build a powerful profile in 5 easy steps
                </h3>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl bg-white p-4 shadow-sm">
                    <p className="text-sm font-semibold" style={{ color: '#001489' }}>1. Start with one powerful profile</p>
                    <p className="mt-1 text-sm text-muted-foreground">Zamzi Connect hardwires your profile into Google, Bing, and AI. We push your data where it matters most, so you land the clients, grow your business, and get opportunities all at once.</p>
                  </div>
                  <div className="rounded-xl bg-white p-4 shadow-sm">
                    <p className="text-sm font-semibold" style={{ color: '#001489' }}>2. Share your value</p>
                    <p className="mt-1 text-sm text-muted-foreground">Add a clear headline and bio so people instantly know what you offer.</p>
                  </div>
                  <div className="rounded-xl bg-white p-4 shadow-sm">
                    <p className="text-sm font-semibold" style={{ color: '#001489' }}>3. Add your logo</p>
                    <p className="mt-1 text-sm text-muted-foreground">Upload your business logo and photos of your product for quick wins to build trust fast.</p>
                  </div>
                  <div className="rounded-xl bg-white p-4 shadow-sm">
                    <p className="text-sm font-semibold" style={{ color: '#001489' }}>4. Get your Verification Badge</p>
                    <p className="mt-1 text-sm text-muted-foreground">Earn your trust badge so clients and employers know you are real, vetted, and ready to do business.</p>
                  </div>
                  <div className="rounded-xl bg-white p-4 shadow-sm">
                    <p className="text-sm font-semibold" style={{ color: '#001489' }}>5. Unlock your AI Mentor Rewrite</p>
                    <p className="mt-1 text-sm text-muted-foreground">After payment, your AI Mentor rewrites your profile to make it polished and conversion-ready.</p>
                  </div>
                </div>
              </div>
            </section>
            
            {/* Trust Bar Section */}
            <div className="container mx-auto px-4 mt-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                {/* Item 1: Get Discovered on Google */}
                <div className="flex flex-col items-center justify-center">
                  <Image src="https://firebasestorage.googleapis.com/v0/b/zamzi-connect.firebasestorage.app/o/search.svg?alt=media&token=bdaf070a-b73b-4ba8-9335-d7c7c93a53b0" alt="Magnifying glass icon representing search discovery for South African businesses" width={40} height={40} className="mb-3" loading="lazy" />
                  <h3 className="text-lg font-semibold" style={{ color: '#00004c' }}>Get Found Across Search</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                    Structured profiles help customers, employers, search engines, and AI tools understand what you do and where you operate.
                  </p>
                </div>
                {/* Item 2: Get Booked & Get Paid, Grow */}
                <div className="flex flex-col items-center justify-center">
                  <Image src="https://firebasestorage.googleapis.com/v0/b/zamzi-connect.firebasestorage.app/o/bookings.svg?alt=media&token=4c0d2dc4-1c9f-4311-b07e-006c6e24f68c" alt="Calendar and booking icon representing direct client bookings on Zamzi Connect" width={64} height={64} className="mb-3" loading="lazy" />
                  <h3 className="text-lg font-semibold" style={{ color: '#00004c' }}>Get Booked & Get Paid, Grow</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                    Clients can book you directly with instant notifications and real leads. Our dashboard lets you manage everything in one place—focus on making money, not admin.
                  </p>
                </div>
                {/* Item 3: Verified & Trusted */}
                <div className="flex flex-col items-center justify-center">
                  <Image src="https://firebasestorage.googleapis.com/v0/b/zamzi-connect.firebasestorage.app/o/TRUSTED.svg?alt=media&token=f9c382b9-f1cd-40a7-871e-cd4c4d97aed9" alt="Verified trust badge icon for Zamzi Connect verified profiles" width={64} height={64} className="mb-3" loading="lazy" />
                  <h3 className="text-lg font-semibold" style={{ color: '#00004c' }}>Verified & Trusted</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                    Build your digital profile, then verify your profile for real reviews, real security, and real results.
                  </p>
                </div>
              </div>
            </div>
            {/* Job Seekers AI CV Card */}
            <div className="container mx-auto px-4 mt-8 flex justify-center">
              <Card className="max-w-xl w-full shadow-lg border-none bg-gradient-to-br from-blue-50 via-white to-pink-50">
                <CardHeader className="items-center text-center">
                  <Image src="https://firebasestorage.googleapis.com/v0/b/zamzi-connect.firebasestorage.app/o/CV.svg?alt=media&token=dc06b244-56e4-4552-b69d-3649d6b3b164" alt="AI-powered CV creator icon for South African job seekers" width={64} height={64} className="mb-4" loading="lazy" />
                  <CardTitle className="text-2xl font-bold text-primary mt-2">
                    World-Class AI CV Creator for Job Seekers
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-md text-muted-foreground max-w-lg mx-auto mb-4">
                    Stand out with an AI-powered, ATS-friendly (Applicant Tracking System) CV designed to beat the filters and get you hired. Build your future with Zamzi Connect’s world-class technology.
                  </p>
                  <Button asChild size="lg" style={{ backgroundColor: '#ffb81c', color: 'white', fontWeight: 'bold' }}>
                    <Link href="/for-job-seekers">Explore Job Seeker Plans</Link>
                                    </Button>
                </CardContent>
              </Card>
            </div>
        </div> {/* end hero section */}

      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4 flex justify-center">
          <Card className="max-w-2xl w-full shadow-lg border-none bg-white">
            <CardHeader className="items-center text-center">
              <Image src="https://firebasestorage.googleapis.com/v0/b/zamzi-connect.firebasestorage.app/o/everyone.svg?alt=media&token=7c8e6716-c0f6-4a91-9ac8-f7c46292eefa" alt="Icon representing Zamzi Connect for every South African business and professional" width={48} height={48} className="mb-4" loading="lazy" />
              <CardTitle className="text-2xl font-bold text-primary mt-2">
                Zamzi Connect is For Everyone
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-md text-muted-foreground max-w-lg mx-auto">
                Zamzi Connect is a movement for every South African business owner and worker. We help people build stronger digital profiles that can be found on Google, understood by AI, and trusted by the people ready to act. From professional firms to skilled artisans, get direct bookings and manage your leads without the admin stress. Stand out, get found, and focus on your work in any industry.<br /><br />
                <span style={{ fontWeight: 'bold', display: 'block' }}>
                  <span style={{ color: '#11C629' }}>Be Found</span>.
                  <span style={{ color: '#11C629' }}> Be Trusted</span>.
                  <span style={{ color: '#11C629' }}> Be Chosen</span>.
                </span>
              </p>
              <div className="mt-6 flex flex-col items-center gap-4">
                <Button asChild>
                  <Link href="/for-everyone">Check this out</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      
      <AssistantTeaserSection />

      <div style={{ backgroundColor: '#fff6e5' }} id="features">
        <FeaturesSection />
      </div>

      <div id="why-zamzi">
        <WhyChooseZamziSection />
      </div>

      <div id="vetting">
        <VettingSystemSection />
      </div>
      
      <div id="vibe">
        <ZamziVibeSection />
      </div>

      <section className="bg-[#f8fbff] py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl rounded-3xl border border-[#d7deef] bg-white p-6 shadow-[0_24px_48px_-30px_rgba(15,23,42,0.16)] md:p-8">
            <div className="text-center">
              <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
                <Link href="/discoverability-platform-south-africa" className="font-semibold text-[#001489] underline underline-offset-4">
                  What makes Zamzi different?
                </Link>
                <Link href="/resources" className="font-semibold text-[#001489] underline underline-offset-4">
                  Discoverability guides
                </Link>
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: '#214186' }}>
                Learn The Category
              </p>
              <h3 className="mt-2 text-2xl font-bold" style={{ color: '#00004c' }}>
                Understand the shift Zamzi is building for.
              </h3>
              <p className="mt-3 mx-auto max-w-3xl text-sm md:text-base" style={{ color: '#555555' }}>
                These are not product sign-up pages. They explain why discoverability, Google visibility, and AI readiness now matter for South African businesses and professionals.
              </p>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <Card className="border border-[#dde5f1] bg-white shadow-none">
                <CardHeader>
                  <CardTitle className="text-xl" style={{ color: '#00004c' }}>
                    AI Visibility Platform South Africa
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Explains how Zamzi is positioning itself for the AI recommendation era, where clearer facts and stronger trust signals shape who gets mentioned.
                  </p>
                  <div className="mt-4">
                    <Button asChild variant="outline">
                      <Link href="/ai-visibility-platform-south-africa">Read the AI visibility page</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-[#dde5f1] bg-white shadow-none">
                <CardHeader>
                  <CardTitle className="text-xl" style={{ color: '#00004c' }}>
                    How To Get Found on Google in South Africa
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    A direct explanation of what helps businesses and professionals show up more clearly on Google through better service language, local context, and trust.
                  </p>
                  <div className="mt-4">
                    <Button asChild variant="outline">
                      <Link href="/how-to-get-found-on-google-in-south-africa">Read the Google visibility page</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
      
      <div id="testimonials">
        <TestimonialsSection />
      </div>
      
    </div>
  );
}
